import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, ArrowRight } from 'lucide-react';
import { blogPosts as mockBlogPosts } from '../data/mock';

const BlogPage = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [categories, setCategories] = useState(['All']);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const url = selectedCategory === 'All' 
          ? `${API_URL}/api/blog`
          : `${API_URL}/api/blog?category=${encodeURIComponent(selectedCategory)}`;
        
        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          setBlogPosts(data);
          
          // Only fetch categories once on initial load
          if (categories.length === 1) {
            const allResponse = await fetch(`${API_URL}/api/blog`);
            if (allResponse.ok) {
              const allData = await allResponse.json();
              const uniqueCategories = ['All', ...new Set(allData.map(post => post.category))];
              setCategories(uniqueCategories);
            }
          }
        } else {
          // Fallback to mock data
          setBlogPosts(mockBlogPosts);
          setCategories(['All', ...new Set(mockBlogPosts.map(post => post.category))]);
        }
      } catch (error) {
        console.error('Error fetching blog posts:', error);
        // Fallback to mock data
        setBlogPosts(mockBlogPosts);
        setCategories(['All', ...new Set(mockBlogPosts.map(post => post.category))]);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogPosts();
  }, [selectedCategory, categories.length]);

  const filteredPosts = selectedCategory === 'All' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="section" style={{ background: 'var(--gradient-hero-subtle)', paddingTop: '4rem' }}>
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="heading-hero mb-4">Blog & Resources</h1>
            <p className="body-large mb-6" style={{ color: 'var(--text-secondary)' }}>
              Practical guidance, parenting tips, and insights on building emotional intelligence at every age.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="section" style={{ background: 'var(--bg-page)', paddingTop: '2rem' }}>
        <div className="container">
          <div className="flex gap-3 flex-wrap justify-center mb-8">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === category
                    ? 'bg-text-primary text-white'
                    : 'bg-glass-bg border border-glass-border hover:border-text-primary'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Blog Posts Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map(post => (
              <Link 
                key={post.id}
                to={`/blog/${post.slug}`}
                className="card hover-lift"
                style={{ padding: 0, overflow: 'hidden' }}
              >
                <img 
                  src={post.image}
                  alt={post.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <div className="flex items-center gap-4 mb-3">
                    <span className="caption px-3 py-1 rounded-full" style={{ background: 'var(--accent-blue-200)' }}>
                      {post.category}
                    </span>
                    <div className="flex items-center gap-1 caption">
                      <Calendar size={14} />
                      {new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </div>
                  </div>
                  <h3 className="heading-3 mb-2">{post.title}</h3>
                  <p className="body-small mb-4" style={{ color: 'var(--text-secondary)' }}>
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 caption">
                      <User size={14} />
                      {post.author}
                    </div>
                    <span className="text-sm font-medium" style={{ color: 'var(--brand-orange)' }}>
                      Read More <ArrowRight size={16} className="inline" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="section" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <div className="max-w-2xl mx-auto card text-center">
            <h2 className="heading-2 mb-4">Get Weekly Parenting Tips</h2>
            <p className="body-large mb-6" style={{ color: 'var(--text-secondary)' }}>
              Subscribe to receive practical emotional intelligence strategies and free resources.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Your email address"
                className="flex-1 px-4 py-3 rounded-full border border-border-input focus:border-border-input-focus outline-none"
              />
              <button className="btn-primary whitespace-nowrap">
                Subscribe
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
